/**
 * Pull request reviews, as pure rules: what a pull request's row says on the
 * reviews page, the state the review sheet names, which Team is the catch-all
 * Reviews team, which repositories are still unowned, what Proctor discarded
 * and why, and what the Team queue's "Also today" line says.
 *
 * The page has two sections, both paged by the server: Held for you (Proctor
 * drafted a review and holds it for a person) and All pull requests (every
 * other one, newest activity first). A row says its own state, because no
 * group heading says it for it.
 */
import { reviewListStatusOf, type ReviewListStatus } from "@yagni/shared";
import type {
  CodeReviewReport,
  CodeReviewWatchedPr,
  RepoClaimMove,
  RepoClaimRow,
  ReviewFinding,
  WorkerEngagementSummary,
} from "@yagni/shared";
import { notCarriedLine } from "../workers/copy.js";
import { buildPipeline, decisionFor, rosterNote } from "../work/review/pipeline.js";
import { shortAge } from "../ticket/queueGroups.js";
import type { HarperRunRow } from "../workers/useWorkers.js";

export type ReviewState = "held" | "reviewing" | "followup" | "posted" | "closed";

/** One watched pull request on the page, with the Team whose listing carried it. */
export interface ReviewRow extends CodeReviewWatchedPr {
  deskId: string;
  deskName: string | null;
  /** The repository is still on the catch-all Reviews team. */
  unowned: boolean;
}

export const REVIEW_STATE_PILL: Record<ReviewState, { label: string; kind: "needs" | "live" | "stop" | "done" }> = {
  held: { label: "Held for you", kind: "needs" },
  reviewing: { label: "Reviewing", kind: "live" },
  followup: { label: "Needs follow-up", kind: "stop" },
  posted: { label: "Posted", kind: "done" },
  closed: { label: "Closed", kind: "done" },
};

const DAY_MS = 86_400_000;
const CLOSED_WINDOW_DAYS = 7;

function timeOf(iso: string | null | undefined): number {
  const at = iso ? Date.parse(iso) : Number.NaN;
  return Number.isFinite(at) ? at : 0;
}

export function startOfDay(now: number): number {
  const day = new Date(now);
  day.setHours(0, 0, 0, 0);
  return day.getTime();
}

export function isToday(iso: string | null | undefined, now: number): boolean {
  const at = timeOf(iso);
  return at > 0 && at >= startOfDay(now);
}

/** Which group a watched pull request prints in, or null when it prints in none. */
export function reviewStateOf(pr: CodeReviewWatchedPr, now: number): ReviewState | null {
  if (pr.githubStatus === "merged" || pr.githubStatus === "closed") {
    return timeOf(pr.updatedAt) >= startOfDay(now) - CLOSED_WINDOW_DAYS * DAY_MS ? "closed" : null;
  }
  if (pr.githubStatus === "draft") return null;
  switch (pr.proctorStatus) {
    case "held_for_you":
      return "held";
    case "reviewing":
    case "queued":
      return "reviewing";
    case "waiting":
      return isToday(pr.updatedAt, now) ? "reviewing" : null;
    case "failed":
    case "needs_follow_up":
      return "followup";
    case "reviewed":
      return isToday(pr.lastReviewedAt, now) ? "posted" : null;
  }
}

/** The repository's own name, without its owner. */
export function repoName(fullName: string): string {
  return fullName.split("/").pop() ?? fullName;
}

/** "TVI · CoreX · Michal G. · round 1", "unowned · Reviews team · Kim T. · follow-up round". */
export function reviewStateLine(row: ReviewRow): string {
  return [
    row.unowned ? "unowned · Reviews team" : (row.deskName ?? "a Team you cannot see"),
    row.author ?? "unknown author",
    row.followUp ? "follow-up round" : "round 1",
  ].join(" · ");
}

/** What one row of the list says about itself: its state word, its dot, and the verb it opens with. */
export interface ListRowState {
  status: ReviewListStatus;
  word: string;
  color: string;
  dot: { color: string; mode: "wait" | "live" | "stop" | null };
  verb: string;
}

/**
 * What a posted review did on GitHub, as a row word: approved by the YAGNI
 * Review App (a Team's own pull request), approved by YAGNI's own App, or
 * comments only.
 */
export function postedWord(githubApproval: CodeReviewWatchedPr["githubApproval"]): string {
  if (githubApproval === "review_app") return "approved on GitHub by YAGNI Review";
  if (githubApproval === "app") return "approved on GitHub";
  return "posted";
}

export function listRowState(
  pr: Pick<CodeReviewWatchedPr, "githubStatus" | "proctorStatus" | "githubApproval">,
): ListRowState {
  const status = reviewListStatusOf(pr.githubStatus, pr.proctorStatus);
  switch (status) {
    case "held":
      return { status, word: "held for you", color: "var(--accent)", dot: { color: "var(--accent)", mode: "wait" }, verb: "Review" };
    case "reviewing": {
      const word = pr.proctorStatus === "queued" ? "starting" : "reviewing now";
      return { status, word, color: "var(--accent-active)", dot: { color: "var(--accent-active)", mode: "live" }, verb: "Watch" };
    }
    case "follow_up": {
      const word = pr.proctorStatus === "failed" ? "the last attempt failed" : "needs follow-up";
      return { status, word, color: "var(--honesty)", dot: { color: "var(--honesty)", mode: "stop" }, verb: "Why" };
    }
    case "posted":
      return { status, word: postedWord(pr.githubApproval), color: "var(--text-tertiary)", dot: { color: "var(--text-tertiary)", mode: null }, verb: "Open" };
    case "waiting":
      return { status, word: "waiting its turn", color: "var(--text-tertiary)", dot: { color: "var(--text-quiet)", mode: null }, verb: "Open" };
    case "draft":
    case "merged":
    case "closed":
      return { status, word: status, color: "var(--text-tertiary)", dot: { color: "var(--text-quiet)", mode: null }, verb: "Open" };
  }
}

/** The Status chip's choices, in menu order. Held has its own section, so it is not one. */
export const REVIEW_STATUS_MENU: readonly { value: Exclude<ReviewListStatus, "held">; label: string }[] = [
  { value: "reviewing", label: "Reviewing" },
  { value: "follow_up", label: "Needs follow-up" },
  { value: "posted", label: "Posted" },
  { value: "waiting", label: "Waiting its turn" },
  { value: "merged", label: "Merged" },
  { value: "closed", label: "Closed" },
  { value: "draft", label: "Draft" },
];

/** How long the row has been like this, in the queue's short form: since its review, or its last change. */
export function reviewAge(row: ReviewRow, now: number): string {
  const status = reviewListStatusOf(row.githubStatus, row.proctorStatus);
  const reviewed = status === "held" || status === "posted" || status === "follow_up";
  return shortAge(reviewed ? (row.lastReviewedAt ?? row.updatedAt) : row.updatedAt, now);
}

/* ── who reviews what ─────────────────────────────────────────────────── */

export interface DirectoryEngagements {
  slug: string;
  engagements: readonly WorkerEngagementSummary[];
}

export interface ReviewingTeams {
  /** Teams Proctor reviews on (engaged, not archived). */
  reviewing: string[];
  /**
   * The catch-all Reviews team: a Team Proctor reviews on with no other
   * Agent engaged, the "Pull request reviews" size of Create a Team. Its
   * repositories are reviewed for everyone and tuned by nobody.
   */
  catchAll: string[];
}

export function reviewingTeamsOf(
  directory: readonly DirectoryEngagements[],
  proctorSlug: string,
  readable?: ReadonlySet<string>,
): ReviewingTeams {
  const live = (engagement: WorkerEngagementSummary) => engagement.enabled && !engagement.archived;
  const proctor = directory.find((entry) => entry.slug === proctorSlug);
  const reviewing = Array.from(
    new Set((proctor?.engagements ?? []).filter(live).map((engagement) => engagement.deskId)),
  ).filter((deskId) => !readable || readable.has(deskId));
  const others = new Set<string>();
  for (const entry of directory) {
    if (entry.slug === proctorSlug) continue;
    for (const engagement of entry.engagements) if (live(engagement)) others.add(engagement.deskId);
  }
  return { reviewing, catchAll: reviewing.filter((deskId) => !others.has(deskId)) };
}

export interface RepoOwnership {
  total: number;
  owned: number;
  /** Claims the catch-all Reviews team still holds. */
  unowned: RepoClaimRow[];
}

export function repoOwnershipOf(claims: readonly RepoClaimRow[], catchAll: readonly string[]): RepoOwnership {
  const pool = new Set(catchAll);
  const unowned = claims.filter((claim) => claim.deskId != null && pool.has(claim.deskId));
  return { total: claims.length, owned: claims.length - unowned.length, unowned };
}

/** The move sheet's choice for a repository: a Team id, or keep it where it is. */
export const KEEP_ON_REVIEWS_TEAM = "__keep__";

/** The moves the sheet posts: every repository whose choice is a Team. */
export function planMoves(choices: ReadonlyMap<string, string>): RepoClaimMove[] {
  return Array.from(choices.entries())
    .filter(([, choice]) => choice !== KEEP_ON_REVIEWS_TEAM)
    .map(([repo, toDeskId]) => ({ repo, toDeskId }));
}

/** Why a move failed, in a person's words. */
export const MOVE_FAILURE_COPY: Record<string, string> = {
  invalid_move: "That is not a repository and Team this can move.",
  duplicate_repo: "Listed twice in one move.",
  team_not_found: "That Team is gone or you cannot see it.",
  forbidden: "You need to own that Team, or manage Agents, to move reviews onto it.",
  not_claimed: "Nobody reviews it any more, so there was nothing to move.",
  claim_takeover_admin_only: "Another Team reviews it. Only a workspace admin can take it over.",
  move_failed: "The move did not save. Try again.",
};

/* ── the review record ────────────────────────────────────────────────── */

export function verdictWord(verdict: CodeReviewReport["verdict"]): string | null {
  if (verdict === "approve") return "would approve";
  if (verdict === "comment_only") return "would comment only";
  return null;
}

/** The round a pull request is on: every pass that delivered a review, at least one. */
export function roundOf(reports: readonly CodeReviewReport[]): number {
  const delivered = reports.filter((report) => report.status === "posted" || report.status === "held" || report.status === "parked");
  return Math.max(1, delivered.length);
}

/** The findings a posted review carried: the editor's survivors minus what verification refuted. */
export function deliveredFindingsOf(report: CodeReviewReport | null): ReviewFinding[] {
  const decisions = report?.refinementDecisions;
  if (!decisions) return [];
  const refuted = new Set(
    (decisions.verifications ?? []).filter((v) => v.verdict === "refuted" && v.failed !== true).map((v) => v.id),
  );
  return decisions.survivors.filter((finding) => !refuted.has(finding.id));
}

export interface DiscardedFinding {
  key: string;
  finding: ReviewFinding;
  /** The check that raised it, as the pipeline names it. */
  stage: string;
  /** Why it did not post, in the record's own words. */
  why: string;
}

/**
 * What Proctor threw away and why: every candidate the editor dropped (with
 * its recorded reason), and every survivor verification refuted against the
 * checkout. Only recorded decisions print; a report with no refinement record
 * discarded nothing this page can name.
 */
export function discardedOf(report: CodeReviewReport | null): DiscardedFinding[] {
  if (!report?.refinementDecisions) return [];
  const decisions = report.refinementDecisions;
  const out: DiscardedFinding[] = [];
  for (const entry of buildPipeline(report)) {
    if (entry.key === "refinement" || entry.key === "verification") continue;
    for (const finding of entry.result?.findings ?? []) {
      const decision = decisionFor(decisions, entry.key, finding.id);
      if (decision?.decision !== "dropped") continue;
      out.push({ key: `${entry.key}:${finding.id}`, finding, stage: entry.label, why: notCarriedLine(decision) });
    }
  }
  for (const verification of decisions.verifications ?? []) {
    if (verification.verdict !== "refuted" || verification.failed === true) continue;
    const finding = decisions.survivors.find((survivor) => survivor.id === verification.id);
    if (!finding) continue;
    out.push({
      key: `verification:${finding.id}`,
      finding,
      stage: "verification",
      why: verification.note ? `refuted against the checkout · ${verification.note}` : "refuted against the checkout",
    });
  }
  return out;
}

export interface CheckRan {
  key: string;
  label: string;
  note: string;
  tone: "ok" | "note" | "quiet";
}

/** The specialists that ran on the newest pass, each with its outcome. */
export function checksOf(report: CodeReviewReport | null): CheckRan[] {
  if (!report) return [];
  return buildPipeline(report).map((entry) => ({
    key: entry.key,
    label: entry.label,
    note: rosterNote(entry, report),
    tone: entry.state === "failed" || entry.findingsCount > 0 ? "note" : entry.state === "running" || entry.state === "done" ? "ok" : "quiet",
  }));
}

/** Severity → the stamp's colour token. */
export function severityColor(severity: string | null): string {
  switch ((severity ?? "").toLowerCase()) {
    case "risk":
    case "high":
    case "critical":
      return "var(--incident)";
    case "decision":
    case "medium":
      return "var(--honesty)";
    case "advise":
    case "low":
      return "var(--text-tertiary)";
    default:
      return "var(--text-quiet)";
  }
}

/* ── "Also today" on the Team queue ───────────────────────────────────── */

export interface AlsoToday {
  reviews: CodeReviewWatchedPr[];
  posts: HarperRunRow[];
  /** "Proctor reviewed 3 pull requests on your repositories · Harper posted work log and deploy notes". */
  line: string;
}

/** A Harper run posted today: settled clean, delivered (or created) since midnight. */
export function postedToday(run: HarperRunRow, now: number): boolean {
  return run.status === "completed" && isToday(run.deliveredAt ?? run.createdAt, now);
}

/** A review Proctor finished on its own today (posted, or settled on a merged or closed pull request). */
export function reviewedToday(pr: CodeReviewWatchedPr, now: number): boolean {
  if (!isToday(pr.lastReviewedAt, now)) return false;
  return pr.proctorStatus === "reviewed" || pr.githubStatus === "merged" || pr.githubStatus === "closed";
}

export function alsoTodayOf(prs: readonly CodeReviewWatchedPr[], runs: readonly HarperRunRow[], now: number): AlsoToday {
  const reviews = prs.filter((pr) => reviewedToday(pr, now));
  const posts = runs.filter((run) => postedToday(run, now));
  const kinds = Array.from(new Set(posts.map((run) => run.jobName.toLowerCase()).filter(Boolean)));
  const parts = [
    reviews.length > 0
      ? `Proctor reviewed ${reviews.length} pull ${reviews.length === 1 ? "request" : "requests"} on your repositories`
      : "",
    kinds.length > 0 ? `Harper posted ${joinWords(kinds)}` : "",
  ].filter(Boolean);
  return { reviews, posts, line: parts.join(" · ") };
}

function joinWords(words: string[]): string {
  if (words.length <= 1) return words.join("");
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}
